import { Component, inject } from '@angular/core';

import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';

import { AgrDateValidators } from '@next/core-lib/form';

import {
  EditManualSoilAnalysis,
  ManualSoilAnalysisDialogModel,
} from './manual-soil-analysis-dialog.model';

import { ManualSoilAnalysis } from '../../models/soil-analysis.model';

@Component({
  selector: 'app-manual-soil-analysis-dialog',
  standalone: false,
  templateUrl: './manual-soil-analysis-dialog.component.html',
})
export class ManualSoilAnalysisDialogComponent extends AbstractFormDialog<ManualSoilAnalysis> {
  data = inject<ManualSoilAnalysisDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<ManualSoilAnalysisDialogComponent, AbstractDialogResultData<ManualSoilAnalysis>>
    >(MatDialogRef);
  private readonly fb = inject(FormBuilder);

  formGroup = this.fb.group({
    id: this.data.manualSoilAnalysis.id,
    description: [this.data.manualSoilAnalysis.description, [Validators.required]],
    samplingDateTime: [
      new Date(this.data.manualSoilAnalysis.samplingDateTime),
      [Validators.required, AgrDateValidators.maxDate(new Date(9999, 12, 31))],
    ],
    characteristics: this.fb.group({
      carbonToNitrogenRatio: [
        this.data.manualSoilAnalysis.characteristics.carbonToNitrogenRatio,
        [Validators.min(0), Validators.max(100)],
      ],
      deliverableNitrogen: [
        this.data.manualSoilAnalysis.characteristics.deliverableNitrogen,
        [Validators.min(0), Validators.max(500)],
      ],
      totalNitrogen: [
        this.data.manualSoilAnalysis.characteristics.totalNitrogen,
        [Validators.min(0)],
      ],
      minNitrogen: [this.data.manualSoilAnalysis.characteristics.minNitrogen, [Validators.min(0)]],
      phosphorusAmmoniumLactate: [
        this.data.manualSoilAnalysis.characteristics.phosphorusAmmoniumLactate,
        [Validators.min(0), Validators.max(500)],
      ],
      phosphorusNumber: [
        this.data.manualSoilAnalysis.characteristics.phosphorusNumber,
        [Validators.min(0), Validators.max(500)],
      ],
      plantAvailablePhosphorus: [
        this.data.manualSoilAnalysis.characteristics.plantAvailablePhosphorus,
        [Validators.min(0)],
      ],
      potassiumNumber: [
        this.data.manualSoilAnalysis.characteristics.potassiumNumber,
        [Validators.min(0), Validators.max(200)],
      ],
      plantAvailableMagnesium: [
        this.data.manualSoilAnalysis.characteristics.plantAvailableMagnesium,
        [Validators.min(0)],
      ],
      calciumCarbonate: [
        this.data.manualSoilAnalysis.characteristics.calciumCarbonate,
        [Validators.min(0), Validators.max(100)],
      ],
      organicMatter: [
        this.data.manualSoilAnalysis.characteristics.organicMatter,
        [Validators.min(0), Validators.max(100)],
      ],
      soilAcidityNir: [
        this.data.manualSoilAnalysis.characteristics.soilAcidityNir,
        [Validators.min(0), Validators.max(15)],
      ],
      soilAcidity: [
        this.data.manualSoilAnalysis.characteristics.soilAcidity,
        [Validators.min(0), Validators.max(15)],
      ],
      ammoniaStock: [
        this.data.manualSoilAnalysis.characteristics.ammoniaStock,
        [Validators.min(0), Validators.max(250)],
      ],
      nitrateStock: [
        this.data.manualSoilAnalysis.characteristics.nitrateStock,
        [Validators.min(0), Validators.max(250)],
      ],
      plantAvailablePotassium: [
        this.data.manualSoilAnalysis.characteristics.plantAvailablePotassium,
        [Validators.min(0), Validators.max(1000)],
      ],
      plantAvailableCalcium: [
        this.data.manualSoilAnalysis.characteristics.plantAvailableCalcium,
        [Validators.min(0), Validators.max(2500)],
      ],
      calciumStock: [
        this.data.manualSoilAnalysis.characteristics.calciumStock,
        [Validators.min(0), Validators.max(10000)],
      ],
      copperStock: [
        this.data.manualSoilAnalysis.characteristics.copperStock,
        [Validators.min(0), Validators.max(2500)],
      ],
      ferroStock: [
        this.data.manualSoilAnalysis.characteristics.ferroStock,
        [Validators.min(0), Validators.max(25000)],
      ],
      cationExchangeCapacity: [
        this.data.manualSoilAnalysis.characteristics.cationExchangeCapacity,
        [Validators.min(0), Validators.max(100)],
      ],
      calciumFilling: [
        this.data.manualSoilAnalysis.characteristics.calciumFilling,
        [Validators.min(0), Validators.max(100)],
      ],
      magnesiumFilling: [
        this.data.manualSoilAnalysis.characteristics.magnesiumFilling,
        [Validators.min(0), Validators.max(100)],
      ],
      potassiumFilling: [
        this.data.manualSoilAnalysis.characteristics.potassiumFilling,
        [Validators.min(0), Validators.max(100)],
      ],
      sodiumFilling: [
        this.data.manualSoilAnalysis.characteristics.sodiumFilling,
        [Validators.min(0), Validators.max(100)],
      ],
      soilAvailablePotassium: [
        this.data.manualSoilAnalysis.characteristics.soilAvailablePotassium,
        [Validators.min(0)],
      ],
      potassiumStock: [
        this.data.manualSoilAnalysis.characteristics.potassiumStock,
        [Validators.min(0)],
      ],
      cecClayHumus: [
        this.data.manualSoilAnalysis.characteristics.cecClayHumus,
        [Validators.min(0)],
      ],
    }),
  });

  confirmManualSoilAnalysis(data: EditManualSoilAnalysis) {
    this.confirm({
      ...data,
      samplingDateTime: new Date(
        Date.UTC(
          data.samplingDateTime.getFullYear(),
          data.samplingDateTime.getMonth(),
          data.samplingDateTime.getDate(),
        ),
      ).toISOString(),
    });
  }
}
